import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import { usePreferences } from './usePreferences';
import { apiErrorMessage } from './prescriptionForm';
import {
  validateMaxForm,
  buildMaxRequest,
  previewPercent,
  explainEvent,
  eventLabel,
  summariseOutbox,
  formatMax,
} from './trainingData';
import '../styles/MyTraining.css';

// An athlete's own training data.
//
// Two things live here because they are both facts about the person rather than
// about a programme: what they can lift, and whether their training may be used
// to write future programmes.
//
// The sharing panel shows what was NOT sent, and why. A consent mechanism whose
// effects are invisible is not much of one — and several of the reasons are
// limitations of the receiving system rather than anything the athlete did, so
// saying "not shared" without saying why reads as a failure of theirs.

const MyTraining = () => {
  const { token } = useAuth();
  const { unit } = usePreferences();

  const [maxes, setMaxes] = useState([]);
  const [consent, setConsent] = useState(false);
  const [events, setEvents] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState({ exerciseName: '', value: '', testedOn: '' });
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState('');

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    try {
      const [maxRes, consentRes, outboxRes] = await Promise.all([
        axios.get(buildApiUrl('/maxes'), authConfig()),
        axios.get(buildApiUrl('/consent'), authConfig()),
        axios.get(buildApiUrl('/feedback/outbox'), authConfig()),
      ]);
      setMaxes(maxRes.data?.maxes ?? []);
      setConsent(Boolean(consentRes.data?.learningConsent));
      setEvents(outboxRes.data?.events ?? []);
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'Could not load your training data.') });
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    load();
  }, [load]);

  // The library is searched rather than listed: 2,765 exercises is not a picker.
  useEffect(() => {
    if (search.trim().length < 2) {
      setExercises([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(
          buildApiUrl(`/exercises/search?q=${encodeURIComponent(search.trim())}`),
          authConfig()
        );
        if (!cancelled) setExercises((response.data ?? []).slice(0, 8));
      } catch {
        if (!cancelled) setExercises([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, authConfig]);

  const recordMax = async (event) => {
    event.preventDefault();
    const found = validateMaxForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    setNotice(null);
    try {
      await axios.put(buildApiUrl('/maxes'), buildMaxRequest(form, unit), authConfig());
      await load();
      setForm({ exerciseName: '', value: '', testedOn: '' });
      setSearch('');
      setNotice({
        tone: 'success',
        message: 'Max recorded. Every %1RM prescription for it now shows a load.',
      });
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'Could not record that max.') });
    } finally {
      setBusy(false);
    }
  };

  const setSharing = async (agreed) => {
    setBusy(true);
    setNotice(null);
    try {
      await axios.put(buildApiUrl('/consent'), { learningConsent: agreed }, authConfig());
      await load();
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'Could not change that.') });
    } finally {
      setBusy(false);
    }
  };

  const preview = previewPercent(form.value, 80);

  if (loading) {
    return <BaseLayout><p className="training-status">Loading your training data…</p></BaseLayout>;
  }

  return (
    <BaseLayout>
      <div className="my-training">
        <header className="my-training-header">
          <span className="pill">Your training</span>
          <h1 className="page-title">Maxes and sharing</h1>
        </header>

        {notice && (
          <p
            className={notice.tone === 'error' ? 'status-error' : 'status-success'}
            role="status"
            aria-live="polite"
          >
            {notice.message}
          </p>
        )}

        <section className="card training-panel">
          <h2 className="section-title">Your maxes</h2>
          <p className="meta-copy">
            A prescription of "80%" turns into a load once you record a max here.
            Retesting replaces the old one, and every prescription updates at once.
          </p>

          {maxes.length === 0 ? (
            <p className="meta-copy empty-state-text">No maxes recorded.</p>
          ) : (
            <ul className="training-list">
              {maxes.map((max) => (
                <li key={max.id}>
                  <span>
                    <strong>{max.exerciseName}</strong>
                    <span className="muted"> · {formatMax(max)}</span>
                  </span>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await axios.delete(buildApiUrl(`/maxes/${max.id}`), authConfig());
                        await load();
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form className="training-form" onSubmit={recordMax}>
            <div className="training-field">
              <label htmlFor="max-exercise">Exercise</label>
              <input
                id="max-exercise"
                value={form.exerciseName}
                placeholder="Start typing…"
                onChange={(e) => {
                  setForm({ ...form, exerciseName: e.target.value });
                  setSearch(e.target.value);
                }}
                aria-invalid={Boolean(errors.exerciseName)}
              />
              {exercises.length > 0 && (
                <ul className="training-suggestions">
                  {exercises.map((exercise) => (
                    <li key={exercise.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, exerciseName: exercise.name });
                          setSearch('');
                        }}
                      >
                        {exercise.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {errors.exerciseName && <span className="field-error">{errors.exerciseName}</span>}
            </div>

            <div className="training-field">
              <label htmlFor="max-value">Best single ({unit})</label>
              <input
                id="max-value"
                type="number"
                step="any"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                aria-invalid={Boolean(errors.value)}
              />
              {errors.value && <span className="field-error">{errors.value}</span>}
              {/* A hint while typing, never sent. The server computes the real
                  one against the stored max. */}
              {preview !== null && !errors.value && (
                <span className="muted training-preview">80% would be {preview} {unit}</span>
              )}
            </div>

            <div className="training-field">
              <label htmlFor="max-tested">Tested on</label>
              <input
                id="max-tested"
                type="date"
                value={form.testedOn}
                onChange={(e) => setForm({ ...form, testedOn: e.target.value })}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={busy}>Record max</button>
          </form>
        </section>

        <section className="card training-panel">
          <h2 className="section-title">Sharing your training</h2>
          <p className="meta-copy">
            Your logged sets can be sent back to the model that writes programmes,
            so it learns from what actually happened. It is off unless you turn it
            on, and you can turn it off again at any time.
          </p>

          <div className="training-consent">
            <button
              type="button"
              className={consent ? 'btn-primary' : 'btn-ghost'}
              disabled={busy}
              aria-pressed={consent}
              onClick={() => setSharing(!consent)}
            >
              {consent ? 'Sharing is on' : 'Sharing is off'}
            </button>
            <span className="muted">{summariseOutbox(events, consent)}</span>
          </div>

          {events.length > 0 && (
            <ul className="training-list training-events">
              {events.slice(0, 20).map((event) => {
                const explained = explainEvent(event);
                return (
                  <li key={event.ID || event.id || event.eventId}>
                    <span>
                      <strong>{eventLabel(event.eventType)}</strong>
                      <div className={explained.actionable ? 'training-actionable' : 'muted'}>
                        {explained.text}
                      </div>
                    </span>
                    <span className={`training-badge training-badge-${event.status}`}>
                      {event.status}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </BaseLayout>
  );
};

export default MyTraining;
